import { useEffect, useId, useState } from "react";
import { Field, useFormState } from "react-final-form";
import { useText } from "../../../hooks";
import type { Country } from "../../../types/country";
import type { FieldOption, QuestionnaireFormValues } from "../../../types/form";
import type { LangKey } from "../../../types/lang";
import type { Answer, CountryQuestion, SimpleAnswer } from "../../../types/question";
import { convertCountriesToSelectOptions } from "../../../utils/country";
import { isRequired } from "../../../utils/validation";
import { Fieldset, FormHelperText, Legend, ReactSelect } from "../../form-fields";
import { formatFieldName } from "../utils";

type Props = {
	question: CountryQuestion;
	countries: Array<Country>;
};

const CountriesMultiSelect = ({ question, countries }: Props) => {
	// todo: accept validate function as prop and use instead of isRequired?
	const countryOptions = convertCountriesToSelectOptions(countries);
	const [t] = useText();
	const describedById = useId();
	const [hasSubmitFailed, setHasSubmitError] = useState(false);
	const { question: questionText } = question;

	const optionsMapping =
		(currentQuestion: CountryQuestion) =>
		(option: FieldOption): Answer => ({
			answerChoiceId:
				currentQuestion.answerChoices.find((a) => a.value === option.value)?.answerChoiceId || null,
			answerValue: option.value,
		});

	const fieldValues =
		useFormState<QuestionnaireFormValues<SimpleAnswer>>().values[
			formatFieldName(question.questionId)
		] || [];

	const currentValues = countryOptions.filter((o) =>
		fieldValues.some((f) => f.answerValue === o.value),
	);

	return (
		<Fieldset
			aria-invalid={hasSubmitFailed}
			aria-describedby={hasSubmitFailed ? describedById : undefined}
		>
			<Legend>{questionText}</Legend>
			<Field<Array<SimpleAnswer>>
				format={(v) => v || []} // defaults the field to empty array instead of empty string
				name={formatFieldName(question.questionId)}
				validate={(values = []) => isRequired(values.map((v) => v.answerValue))} // todo: maybe accept other validation functions as prop?
			>
				{({ input, meta }) => {
					// biome-ignore lint/correctness/useHookAtTopLevel: Not conditional
					useEffect(() => {
						setHasSubmitError(!!meta.submitFailed && !!meta.error);
					}, [meta]);
					return (
						<>
							<ReactSelect
								value={currentValues}
								isMulti
								menuPortalTarget={null}
								options={countryOptions}
								hideSelectedOptions={false}
								placeholder="Sök"
								onChange={(selectedOptions) => {
									const fieldOption = selectedOptions as FieldOption[];
									input.onChange(fieldOption.map(optionsMapping(question)));
								}}
							/>
							{meta.submitFailed && (
								<FormHelperText error={!!meta.error && meta.submitFailed} id={describedById}>
									{t(meta.error as LangKey)}
								</FormHelperText>
							)}
						</>
					);
				}}
			</Field>
		</Fieldset>
	);
};
export default CountriesMultiSelect;
